import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Save, Trash2, Download, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { PropFirmsEditor } from './PropFirmsEditor';
import { PropFirmAlerts } from './PropFirmAlerts';
import { YouTubeVideosPanel } from './YouTubeVideosPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TYPES = ['link', 'video', 'coupon', 'whatsapp'];
const APPROVAL_FLAGS = [
  ['pricing_approved', 'Plan prices (from live checkout pricing)'],
  ['licensing_approved', 'Licensing rule (2 included + 50% off each extra)'],
  ['performance_approved', 'Performance numbers (from ORB page data)'],
  ['faqs_approved', 'ORB page FAQs'],
];
const input = 'px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm';

const Toggle = ({ checked, onChange, testId }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testId}
    className={`px-2.5 py-1 rounded-full text-xs font-medium ${checked ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-white/10 text-white/50'}`}>
    {checked ? 'Approved' : 'Not approved'}
  </button>
);

const KnowledgeEditor = ({ authHeader, onAlerts }) => {
  const [k, setK] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  useEffect(() => {
    fetch(`${API_URL}/api/admin/support/knowledge`, { headers: authHeader }).then((r) => r.json()).then(setK);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!k) return <Loader2 className="w-5 h-5 text-[#7c3aed] animate-spin" />;
  const setRes = (i, patch) => setK({ ...k, resources: k.resources.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const setFaq = (i, patch) => setK({ ...k, extra_faqs: k.extra_faqs.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  const save = async () => {
    setSaving(true);
    const res = await fetch(`${API_URL}/api/admin/support/knowledge`, { method: 'PUT', headers: { ...authHeader, 'Content-Type': 'application/json' }, body: JSON.stringify(k) });
    if (res.ok) { setK(await res.json()); setSaved('Saved. New conversations use this immediately.'); }
    setSaving(false);
  };
  return (
    <div className="glass rounded-xl p-5 border border-white/5 space-y-5" data-testid="support-knowledge-editor">
      <div>
        <h3 className="text-white font-semibold mb-2">Approved data the agent may use</h3>
        <div className="space-y-2">
          {APPROVAL_FLAGS.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-3 text-sm"><span className="text-white/70">{label}</span>
              <Toggle checked={!!k[key]} onChange={(v) => setK({ ...k, [key]: v })} testId={`support-flag-${key}`} /></div>
          ))}
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-white/70 flex items-center gap-2"><MessageCircle size={14} /> WhatsApp number
              <input className={`${input} w-40 font-mono`} value={k.whatsapp_number?.value || ''} onChange={(e) => setK({ ...k, whatsapp_number: { ...k.whatsapp_number, value: e.target.value.replace(/\D/g, '') } })} data-testid="support-whatsapp-number" /></span>
            <Toggle checked={!!k.whatsapp_number?.approved} onChange={(v) => setK({ ...k, whatsapp_number: { ...k.whatsapp_number, approved: v } })} testId="support-flag-whatsapp" />
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><h3 className="text-white font-semibold">Resource library</h3>
          <button onClick={() => setK({ ...k, resources: [...k.resources, { id: `res-${Date.now()}`, title: '', type: 'link', value: '', topics: '', approved: false }] })} className="text-xs text-[#a78bfa] flex items-center gap-1" data-testid="support-add-resource"><Plus size={12} /> Add</button></div>
        <div className="space-y-2">
          {k.resources.map((r, i) => (
            <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`support-resource-row-${r.id}`}>
              <input className={`${input} md:col-span-3`} placeholder="Title" value={r.title} onChange={(e) => setRes(i, { title: e.target.value })} />
              <select className={`${input} md:col-span-1`} value={r.type} onChange={(e) => setRes(i, { type: e.target.value })}>{TYPES.map((t) => <option key={t} value={t} className="bg-[#0a0a12]">{t}</option>)}</select>
              <input className={`${input} md:col-span-3`} placeholder="URL or coupon code" value={r.value} onChange={(e) => setRes(i, { value: e.target.value })} />
              <input className={`${input} md:col-span-3`} placeholder="Topics that trigger it" value={r.topics} onChange={(e) => setRes(i, { topics: e.target.value })} />
              <div className="md:col-span-2 flex items-center gap-2"><Toggle checked={!!r.approved} onChange={(v) => setRes(i, { approved: v })} testId={`support-resource-approve-${r.id}`} />
                <button onClick={() => setK({ ...k, resources: k.resources.filter((_, j) => j !== i) })}><Trash2 size={14} className="text-white/40 hover:text-red-400" /></button></div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><h3 className="text-white font-semibold">Extra FAQ answers</h3>
          <button onClick={() => setK({ ...k, extra_faqs: [...(k.extra_faqs || []), { q: '', a: '', approved: false }] })} className="text-xs text-[#a78bfa] flex items-center gap-1" data-testid="support-add-faq"><Plus size={12} /> Add</button></div>
        <div className="space-y-2">
          {(k.extra_faqs || []).map((f, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
              <input className={`${input} md:col-span-4`} placeholder="Question" value={f.q} onChange={(e) => setFaq(i, { q: e.target.value })} />
              <textarea className={`${input} md:col-span-6`} rows={2} placeholder="Approved answer" value={f.a} onChange={(e) => setFaq(i, { a: e.target.value })} />
              <div className="md:col-span-2 flex items-center gap-2"><Toggle checked={!!f.approved} onChange={(v) => setFaq(i, { approved: v })} />
                <button onClick={() => setK({ ...k, extra_faqs: k.extra_faqs.filter((_, j) => j !== i) })}><Trash2 size={14} className="text-white/40 hover:text-red-400" /></button></div>
            </div>
          ))}
        </div>
      </div>
      <YouTubeVideosPanel authHeader={authHeader} initialVideos={k.youtube_videos} initialSyncedAt={k.youtube_synced_at} />
      <PropFirmAlerts alerts={k.prop_firm_alerts} authHeader={authHeader} onKnowledge={(nk) => { setK(nk); onAlerts((nk.prop_firm_alerts || []).filter((a) => !a.dismissed).length); }} />
      <PropFirmsEditor authHeader={authHeader} firms={k.prop_firms} onChange={(prop_firms) => setK({ ...k, prop_firms })} />
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} className="btn-purple text-sm px-4 py-2 flex items-center gap-2" data-testid="support-knowledge-save">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        {saved && <span className="text-[#10B981] text-xs">{saved}</span>}
      </div>
    </div>
  );
};

export const AdminSupportTab = ({ authHeader }) => {
  const [convs, setConvs] = useState([]);
  const [filters, setFilters] = useState({ language: '', escalated: '' });
  const [open, setOpen] = useState(null);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  useEffect(() => {
    fetch(`${API_URL}/api/admin/support/knowledge`, { headers: authHeader }).then((r) => r.json())
      .then((k) => setAlertCount((k.prop_firm_alerts || []).filter((a) => !a.dismissed).length)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showKnowledge]);
  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (filters.language) p.append('language', filters.language);
    if (filters.escalated) p.append('escalated', filters.escalated);
    const res = await fetch(`${API_URL}/api/admin/support/conversations?${p}`, { headers: authHeader });
    if (res.ok) setConvs((await res.json()).conversations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = async () => {
    const res = await fetch(`${API_URL}/api/admin/support/export`, { headers: authHeader });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await res.blob());
    a.download = 'ai_support_leads.csv';
    a.click();
  };
  return (
    <div className="space-y-4" data-testid="admin-support-tab">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setShowKnowledge((v) => !v)} className="px-3 py-2 rounded-lg bg-white/5 text-white/70 text-xs" data-testid="support-knowledge-toggle">Knowledge &amp; resources {showKnowledge ? '▲' : '▼'}{alertCount > 0 && <span className="ml-1.5 px-1.5 rounded-full bg-[#F59E0B] text-black font-semibold" data-testid="prop-alerts-count">{alertCount} rule change{alertCount > 1 ? 's' : ''}</span>}</button>
        <select className={input} value={filters.language} onChange={(e) => setFilters({ ...filters, language: e.target.value })} data-testid="support-filter-language">
          <option value="" className="bg-[#0a0a12]">All languages</option><option value="english" className="bg-[#0a0a12]">English</option><option value="hindi" className="bg-[#0a0a12]">Hindi</option><option value="hinglish" className="bg-[#0a0a12]">Hinglish</option>
        </select>
        <select className={input} value={filters.escalated} onChange={(e) => setFilters({ ...filters, escalated: e.target.value })} data-testid="support-filter-escalated">
          <option value="" className="bg-[#0a0a12]">All</option><option value="true" className="bg-[#0a0a12]">Escalated to WhatsApp</option><option value="false" className="bg-[#0a0a12]">Not escalated</option>
        </select>
        <button onClick={exportCsv} className="btn-secondary text-xs flex items-center gap-1" data-testid="support-export"><Download size={12} /> Export CSV</button>
        <span className="text-white/40 text-xs">{convs.length} conversations</span>
      </div>
      {showKnowledge && <KnowledgeEditor authHeader={authHeader} onAlerts={setAlertCount} />}
      {convs.map((c) => {
        const userLines = c.transcript.filter((t) => t.role === 'user');
        return (
          <div key={c.id} className="glass rounded-xl border border-white/5" data-testid={`support-conv-${c.id}`}>
            <button className="w-full p-4 flex items-center justify-between text-left" onClick={() => setOpen(open === c.id ? null : c.id)}>
              <div>
                <p className="text-white font-medium">{c.name} <span className="text-white/40 text-xs ml-2">{c.email} · {c.phone}</span></p>
                <p className="text-white/40 text-xs mt-0.5">{new Date(c.created_at).toLocaleString()} · {c.language} · {userLines.length} questions · High intent
                  {c.escalated && <span className="ml-2 text-[#25D366]">· Escalated to WhatsApp</span>}</p>
                {userLines[0] && <p className="text-white/60 text-xs mt-1 truncate max-w-xl">"{userLines[0].text}"</p>}
              </div>
              {open === c.id ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
            </button>
            {open === c.id && (
              <div className="px-4 pb-4 space-y-1 text-sm" data-testid={`support-transcript-${c.id}`}>
                {c.transcript.length === 0 && <p className="text-white/40">No speech recorded.</p>}
                {c.transcript.map((t, i) => <p key={i} className={t.role === 'user' ? 'text-white/60' : 'text-white'}><span className="text-white/30 mr-1">{t.role === 'user' ? 'Visitor:' : 'Agent:'}</span>{t.text}</p>)}
                {c.shared_resources?.length > 0 && <p className="text-white/40 text-xs pt-2">Shared: {c.shared_resources.join(', ')} · data as of {c.data_as_of}</p>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
